/* eslint-disable max-lines */
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Camera, CameraDirection, CameraResultType, CameraSource } from "@capacitor/camera";
import { type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined, toErrorMessage } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { IGNORE_CAMERA_ERROR_MESSAGES } from "@src/appV2/lib/files/constants";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { checkAndRequestPhotoPermissions } from "@src/appV2/lib/Media/checkAndRequestPhotoPermissions";
import { openNativeSettings } from "@src/appV2/lib/NativeSettings/native-settings";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { parseWorkerNameForBadgePdf } from "../../WorkBadges/utils/badgePdf";
import { useGetAllPossiblePicturesForBadge } from "../useGetAllPossiblePicturesForBadge";
import { useGetBadgePdf } from "../useGetBadgePdf";
import { resetContractorBadgesQuery, useGetContractorBadges } from "../useGetContractorBadges";
import { useSaveBadge } from "../useSaveBadge";
import { BottomSheetContent, type BottomSheetContentProps } from "./BottomSheetContent";
import { NoCameraPermission } from "./NoCameraPermission";

interface GetBadgeBottomSheetProps {
  modalState: UseModalState;
  qualification: string;
  state?: string;
  analyticsMetadata: Record<string, string | boolean | undefined>;
}

interface BadgePictureChoice {
  fileBase64: string;
  type:
    | "profile_picture"
    | "verification_selfie"
    | "last_saved_badge"
    | "photo_from_camera"
    | "empty";
}

const switchButtonLabels = {
  profile_picture: "Switch to Clipboard profile picture",
  verification_selfie: "Switch to Personal ID selfie",
  last_saved_badge: "Switch to last badge photo",
  photo_from_camera: "Switch to previously taken photo",
  empty: "", // Not used but needed for type safety
} as const;

function determineBadgePictureToUse(params: {
  userPictureChoice: BadgePictureChoice | undefined;
  profilePictureBase64: string | undefined;
  lastSavedBadgePictureBase64: string | undefined;
}): BadgePictureChoice {
  const { userPictureChoice, profilePictureBase64, lastSavedBadgePictureBase64 } = params;
  if (userPictureChoice) {
    return userPictureChoice;
  }

  if (lastSavedBadgePictureBase64) {
    return { fileBase64: lastSavedBadgePictureBase64, type: "last_saved_badge" };
  }

  if (profilePictureBase64) {
    return { fileBase64: profilePictureBase64, type: "profile_picture" };
  }

  return { fileBase64: "", type: "empty" };
}

export function GetBadgeBottomSheet(props: GetBadgeBottomSheetProps) {
  const { modalState, qualification, state, analyticsMetadata } = props;
  const worker = useDefinedWorker();
  const { showInfoToast, showErrorToast } = useToast();

  // User can change the badge picture using the tertiary button or upload a new photo
  const [currentPictureChoice, setCurrentPictureChoice] = useState<BadgePictureChoice | undefined>(
    undefined
  );

  // This state keeps track of the picture that the user can switch to using the tertiary button
  const [allowedSwitchChoice, setAllowedSwitchChoice] = useState<BadgePictureChoice | undefined>(
    undefined
  );

  // State for managing the image cropper modal
  const imageCropperModalState = useModalState();
  const [cropperOptions, setCropperOptions] = useState<{
    imageSrc: string;
  }>({
    imageSrc: "",
  });

  const { mutateAsync: saveBadge, status: saveBadgeStatus } = useSaveBadge();

  // We keep this as state because we only want to show error if user takes photo and then it is denied
  const [isCameraPermissionDenied, setIsCameraPermissionDenied] = useState<boolean | undefined>();

  // Get all possible pictures for the badge and their loading statuses
  const {
    profilePictureBase64,
    verificationSelfieBase64,
    lastSavedBadgePictureBase64,
    profilePictureBase64Status,
    verificationSelfieBase64Status,
    lastSavedBadgePictureBase64Status,
    resetQueries,
  } = useGetAllPossiblePicturesForBadge({
    workerId: worker.userId,
    options: {
      // Setting this false to avoid random reload as in this feature modal will go out of focus while sharing badge
      refetchOnWindowFocus: false,
    },
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (modalState.modalIsClosed) {
      setCurrentPictureChoice(undefined);
      setAllowedSwitchChoice(undefined);
      setCropperOptions({
        imageSrc: "",
      });
      resetQueries();
      resetContractorBadgesQuery(queryClient);
    }
  }, [modalState.modalIsClosed, queryClient, resetQueries]);

  // Get badges
  const { data: badgesDetails, status: badgesDetailsStatus } = useGetContractorBadges(
    {
      workerId: worker.userId,
      qualification,
      state,
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  const badgeDetails = badgesDetailsStatus === "success" ? badgesDetails?.data[0] : undefined;

  // Determine the badge picture to use based on feature requirements
  const badgePictureToUse = determineBadgePictureToUse({
    userPictureChoice: currentPictureChoice,
    profilePictureBase64,
    lastSavedBadgePictureBase64,
  });

  const { data: badgePdf, status: badgePdfStatus } = useGetBadgePdf({
    licenseNumber: badgeDetails?.attributes.license?.number,
    qualification: badgeDetails?.attributes.qualification.displayLabel,
    nameDetails: {
      ...parseWorkerNameForBadgePdf(worker),
    },
    badgePicture: badgePictureToUse.fileBase64,
    workerId: worker.userId,
  });

  const isDataLoading =
    badgesDetailsStatus === "loading" ||
    profilePictureBase64Status === "loading" ||
    verificationSelfieBase64Status === "loading" ||
    lastSavedBadgePictureBase64Status === "loading";

  const isError =
    modalState.modalIsOpen && // We only care about this when the modal is open
    !isDataLoading && // We only care about this when the data is not loading, if data is loading some data required will be empty/undefined
    (profilePictureBase64Status === "error" ||
      verificationSelfieBase64Status === "error" ||
      lastSavedBadgePictureBase64Status === "error" ||
      badgesDetailsStatus === "error" ||
      !badgeDetails ||
      !qualification);

  useEffect(() => {
    if (isError) {
      logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_GENERATION_FAILURE, {
        error: new Error("Failed to generate badge"),
        metadata: {
          qualification,
          state,
        },
      });
    }
  }, [isError, qualification, state]);

  const isPdfLoading = badgePdfStatus === "loading";
  if (isDataLoading) {
    return (
      <BottomSheetContent
        isDataLoading={isDataLoading}
        isPdfLoading={isPdfLoading}
        modalState={modalState}
      />
    );
  }

  if (isError) {
    return (
      <BottomSheetContent
        isDataLoading={false}
        isPdfLoading={false}
        modalState={modalState}
        isError={isError}
      />
    );
  }

  const takeNewPhoto = async () => {
    try {
      if (isCapacitorPlatform()) {
        await checkAndRequestPhotoPermissions(CameraSource.Camera);
      }

      setCropperOptions({
        imageSrc: "",
      });
      imageCropperModalState.openModal();

      // Our mobile devices are usually 512px wide, so we take max width as 512px
      // This will ensure reduced image resolution and thus size is reduced
      const maxWidth = 512;
      const { dataUrl } = await Camera.getPhoto({
        source: isCapacitorPlatform() ? CameraSource.Camera : CameraSource.Photos,
        resultType: CameraResultType.DataUrl,
        direction: CameraDirection.Front,

        // Plugin param is not named good.
        // This param is to define max width while preserving aspect ratio of camera image
        width: maxWidth,
      });

      if (!isDefined(dataUrl)) {
        imageCropperModalState.closeModal();
        setCropperOptions({
          imageSrc: "",
        });
        logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_TAKE_PHOTO_EMPTY_IMAGE);
        return;
      }

      setCropperOptions({
        imageSrc: dataUrl,
      });
    } catch (unknownError) {
      imageCropperModalState.closeModal();
      setCropperOptions({
        imageSrc: "",
      });

      // We ignore these errors as they are expected and we don't want to show an error to the user
      if (unknownError instanceof CameraPermissionDeniedError) {
        setIsCameraPermissionDenied(true);
        return;
      }

      if (IGNORE_CAMERA_ERROR_MESSAGES.has(toErrorMessage(unknownError))) {
        return;
      }

      logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_TAKE_PHOTO_FAILURE, {
        error: unknownError,
      });
      showErrorToast("Failed to take photo. Please try again.");
    }
  };

  if (isCameraPermissionDenied) {
    return (
      <NoCameraPermission
        modalState={modalState}
        onGoToSettings={async () => {
          if (isCapacitorPlatform()) {
            // Reset to initial state so that we can try to take a new photo
            // undefined means we don't know if the camera permission is denied or not
            setIsCameraPermissionDenied(undefined);

            await openNativeSettings({ option: "application_details" });
          }
        }}
      />
    );
  }

  const commonCropperProps = {
    modalState: imageCropperModalState,
    imageSrc: cropperOptions.imageSrc,
    onRetry: () => {
      // retry the photo taking
      void takeNewPhoto();
    },
  };

  const saveBadgeButton = {
    label: "Save and Download",
    disabled: !badgePictureToUse.fileBase64 || saveBadgeStatus === "loading",
    isLoading: saveBadgeStatus === "loading",
    onClick: async () => {
      if (!badgePictureToUse.fileBase64) {
        showInfoToast("Please select a photo to save the badge");
        return;
      }

      try {
        logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_SAVE_AND_DOWNLOAD_TAPPED, {
          ...analyticsMetadata,
          licenseNumber: badgeDetails?.attributes.license?.number,
          badgePhotoUsed: badgePictureToUse.type,
        });

        await saveBadge({
          pictureFileBase64: badgePictureToUse.fileBase64,
          workerId: worker.userId,
          firstName: worker.firstName ?? "",
          lastName: worker.lastName ?? "",
          qualification: badgeDetails?.attributes.qualification.name ?? "",
          licenseNumber: badgeDetails?.attributes.license?.number ?? "",
        });

        logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_SAVE_AND_DOWNLOAD_SUCCESS, {
          ...analyticsMetadata,
          licenseNumber: badgeDetails?.attributes.license?.number,
          badgePhotoUsed: badgePictureToUse.type,
        });
      } catch (error) {
        logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_SAVE_BADGE_FAILURE, {
          error,
        });
        showErrorToast("Failed to save badge. Try again. Contact support if the problem persists.");
      }
    },
  };

  const takeNewPhotoButton = {
    label: "Take a new photo",
    onClick: async () => {
      await takeNewPhoto();
    },
  };

  // Allows user to toggle between the current picture and the allowed switch choice
  const switchButton =
    allowedSwitchChoice && currentPictureChoice
      ? {
          label: switchButtonLabels[allowedSwitchChoice.type],
          onClick: () => {
            setAllowedSwitchChoice({
              fileBase64: currentPictureChoice.fileBase64,
              type: currentPictureChoice.type,
            });
            setCurrentPictureChoice({
              fileBase64: allowedSwitchChoice.fileBase64,
              type: allowedSwitchChoice.type,
            });
          },
        }
      : undefined;

  // Initialize with the common props
  let bottomSheetProps: BottomSheetContentProps = {
    modalState,
    isDataLoading,
    isPdfLoading,
    pdfKey: badgePdf?.pdfKey,
    badgePdf: badgePdf?.blob,
    title: `Your ${badgeDetails?.attributes.qualification.displayLabel ?? ""} badge`,
    primaryButton: saveBadgeButton,
    secondaryButton: takeNewPhotoButton,
    tertiaryButton: switchButton,
  };

  // If we have last saved badge picture, we first show the last saved badge picture.
  // - User can take a new photo using secondary button
  // - User can switch b/w the last saved badge picture<->and new photo using tertiary button
  if (lastSavedBadgePictureBase64) {
    bottomSheetProps = {
      ...bottomSheetProps,
      description:
        "We use the photo from the previous badge you created. You may take a new photo.",
      imageUploadCropper: {
        ...commonCropperProps,
        onCropComplete: (newPhoto: string) => {
          setAllowedSwitchChoice({
            fileBase64: lastSavedBadgePictureBase64,
            type: "last_saved_badge",
          });

          setCurrentPictureChoice({ fileBase64: newPhoto, type: "photo_from_camera" });
        },
      },
    };
  } else if (profilePictureBase64 && verificationSelfieBase64) {
    /**
     * If we have both profile picture and verification selfie
     * - We first show the profile picture.
     * - User can take a new photo using secondary button
     * - User can switch b/w the profile picture<->verification selfie using tertiary button.
     */
    bottomSheetProps = {
      ...bottomSheetProps,
      description:
        "We use your Clipboard profile picture to create your badge. You may take a new photo or switch to the selfie from your Personal ID verification.",
      imageUploadCropper: {
        ...commonCropperProps,
        onCropComplete: (newPhotoBase64: string) => {
          setCurrentPictureChoice({ fileBase64: newPhotoBase64, type: "photo_from_camera" });
        },
      },
      tertiaryButton:
        allowedSwitchChoice?.type === "profile_picture"
          ? {
              label: switchButtonLabels.profile_picture,
              onClick: () => {
                setCurrentPictureChoice({
                  fileBase64: profilePictureBase64,
                  type: "profile_picture",
                });

                setAllowedSwitchChoice({
                  fileBase64: verificationSelfieBase64,
                  type: "verification_selfie",
                });
              },
            }
          : {
              label: switchButtonLabels.verification_selfie,
              onClick: () => {
                setCurrentPictureChoice({
                  fileBase64: verificationSelfieBase64,
                  type: "verification_selfie",
                });
                setAllowedSwitchChoice({
                  fileBase64: profilePictureBase64,
                  type: "profile_picture",
                });
              },
            },
    };
  } else if (profilePictureBase64) {
    /**
     * If user has only profile picture we show the profile picture.
     * - User can take a new photo using secondary button
     * - User can switch b/w the last taken photo<->profile picture using tertiary button
     */
    bottomSheetProps = {
      ...bottomSheetProps,
      description:
        "We use your Clipboard profile picture to create your badge. You may take a new photo.",
      imageUploadCropper: {
        ...commonCropperProps,
        onCropComplete: (newPhotoBase64: string) => {
          setAllowedSwitchChoice({
            fileBase64: profilePictureBase64,
            type: "profile_picture",
          });

          setCurrentPictureChoice({ fileBase64: newPhotoBase64, type: "photo_from_camera" });
        },
      },
    };
  } else {
    /**
     * Scenario: User have no profile picture + no last saved badge. May have verification selfie.
     * - User can take a new photo using secondary button
     * - User can switch b/w the last taken photo<->verification selfie using tertiary button if available
     */

    const switchToVerificationSelfieButton =
      verificationSelfieBase64 && currentPictureChoice?.type !== "verification_selfie"
        ? {
            label: switchButtonLabels.verification_selfie,
            onClick: () => {
              if (currentPictureChoice) {
                setAllowedSwitchChoice({
                  fileBase64: currentPictureChoice.fileBase64,
                  type: currentPictureChoice.type,
                });
              }

              setCurrentPictureChoice({
                fileBase64: verificationSelfieBase64,
                type: "verification_selfie",
              });
            },
          }
        : undefined;

    bottomSheetProps = {
      ...bottomSheetProps,
      description: `You don’t have a Clipboard profile picture to use for your badge. Please take a new photo ${
        verificationSelfieBase64 ? "or switch to your Personal ID selfie" : ""
      }.`,
      imageUploadCropper: {
        ...commonCropperProps,
        onCropComplete: (newPhoto: string) => {
          if (verificationSelfieBase64) {
            setAllowedSwitchChoice({
              fileBase64: verificationSelfieBase64,
              type: "verification_selfie",
            });
          }

          setCurrentPictureChoice({ fileBase64: newPhoto, type: "photo_from_camera" });
        },
      },
      primaryButton: badgePictureToUse?.fileBase64 ? saveBadgeButton : takeNewPhotoButton,
      secondaryButton: badgePictureToUse?.fileBase64 ? takeNewPhotoButton : undefined,
      tertiaryButton: switchButton ?? switchToVerificationSelfieButton,
    };
  }

  return <BottomSheetContent {...bottomSheetProps} />;
}
/* eslint-enable max-lines */
